/**
 * Google Drive → Files import rules (docs/features/google-drive-import.md, contracts imports.ts "Google Drive"), shared by
 * the server and the settings screens. Conventions follow Google Takeout and Drive → Dropbox / Box migrations: Google's
 * own formats are exported to Microsoft Office (Drawings to PDF), what Google can't export (Forms, Sites, Maps, Apps
 * Script…) isn't copied, and Drive roles map onto Files roles.
 */
import { type DriveRole, GOOGLE_DRIVE_SCOPE, type GoogleDriveImportOptions, type GoogleDrivePreview } from "@bundle/contracts";
import { GOOGLE_IMPORT_SCOPES } from "./mailIdentity.ts";

export const GOOGLE_FOLDER_MIME = "application/vnd.google-apps.folder";
export const GOOGLE_SHORTCUT_MIME = "application/vnd.google-apps.shortcut";

/** Every scope Bundle's Google import uses, Drive's last: what the Admin console steps' "Copy all" pastes. */
export const GOOGLE_DRIVE_ALL_SCOPES: readonly string[] = [...GOOGLE_IMPORT_SCOPES, GOOGLE_DRIVE_SCOPE];

/** How one Drive item comes over: `blob` downloads its bytes; `export` converts a Google file; `skip` can't. */
export type DrivePlan =
  | { kind: "folder" }
  | { kind: "shortcut" }
  | { kind: "blob" }
  | { kind: "export"; mime: string; ext: string }
  | { kind: "skip" };

const EXPORTS: Record<string, { mime: string; ext: string }> = {
  "application/vnd.google-apps.document": { mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ext: "docx" },
  "application/vnd.google-apps.spreadsheet": { mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ext: "xlsx" },
  "application/vnd.google-apps.presentation": { mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation", ext: "pptx" },
  "application/vnd.google-apps.drawing": { mime: "application/pdf", ext: "pdf" },
  // Jamboard (retired) still exports as PDF.
  "application/vnd.google-apps.jam": { mime: "application/pdf", ext: "pdf" },
};

/** The plan for a Drive item's mimeType. */
export function drivePlan(mimeType: string): DrivePlan {
  if (mimeType === GOOGLE_FOLDER_MIME) return { kind: "folder" };
  if (mimeType === GOOGLE_SHORTCUT_MIME) return { kind: "shortcut" };
  const ex = EXPORTS[mimeType];
  if (ex) return { kind: "export", mime: ex.mime, ext: ex.ext };
  // Forms, Sites, Maps, Apps Script, Vids, site / fusion leftovers: nothing to download.
  if (mimeType.startsWith("application/vnd.google-apps.")) return { kind: "skip" };
  return { kind: "blob" };
}

/**
 * The Files name for a Drive item: characters Files rejects (`/`, `\`, control characters) become `_`, an exported
 * file gets its extension (unless it already ends with it), at most 255 characters (the extension kept), never empty.
 */
export function importedName(name: string, plan: DrivePlan): string {
  let base = "";
  for (const ch of name) {
    const code = ch.charCodeAt(0);
    base += code < 0x20 || code === 0x7f || ch === "/" || ch === "\\" ? "_" : ch;
  }
  base = base.trim();
  if (base === "" || base === "." || base === "..") base = "Untitled";
  const ext = plan.kind === "export" && !base.toLowerCase().endsWith(`.${plan.ext}`) ? `.${plan.ext}` : "";
  const room = 255 - ext.length;
  if (base.length > room) {
    if (ext === "") {
      const dot = base.lastIndexOf(".");
      const tail = dot > 0 && base.length - dot <= 16 ? base.slice(dot) : "";
      base = base.slice(0, room - tail.length) + tail;
    } else base = base.slice(0, room);
  }
  return base + ext;
}

/** A Drive role → the Files role (owner stays owner; organizers and editors edit; commenters comment; readers view). */
export function roleFromGoogle(role: string): DriveRole {
  switch (role) {
    case "owner":
      return "owner";
    case "organizer":
    case "fileOrganizer":
    case "writer":
      return "editor";
    case "commenter":
      return "commenter";
    default:
      return "viewer";
  }
}

const RANK: Record<DriveRole, number> = { viewer: 0, commenter: 1, editor: 2, owner: 3 };

/** One Drive permission as `permissions` lists it. */
export interface GooglePermission {
  type: "user" | "group" | "domain" | "anyone" | string;
  role: string;
  emailAddress?: string;
  domain?: string;
  deleted?: boolean;
}

export interface MappedSharing {
  /** People (members and guests already in the workspace) other than the owner, with their Files role. */
  shares: { userId: string; role: DriveRole }[];
  /** "Anyone with the link" / the domain: Files has no public links, so anyone in this workspace with the link. */
  link: { scope: "workspace"; role: DriveRole } | null;
  /** Addresses (people and groups) with access who aren't in this workspace, for the report. */
  outside: string[];
}

/**
 * A Drive item's permissions → Files sharing. `members` maps lowercased addresses (sign-in emails and aliases) to
 * member ids; `ownerId` is who owns the Files item (left out of the shares). `domains` are the Google Workspace's own
 * domains: domain sharing outside them counts as outside.
 */
export function mapSharing(perms: GooglePermission[], members: Record<string, string>, ownerId: string, domains: string[]): MappedSharing {
  const byUser = new Map<string, DriveRole>();
  let link: DriveRole | null = null;
  const outside = new Set<string>();
  const own = new Set(domains.map((d) => d.toLowerCase()));
  for (const p of perms) {
    if (p.deleted) continue;
    const role = roleFromGoogle(p.role);
    const capped: DriveRole = role === "owner" ? "editor" : role;
    if (p.type === "anyone" || (p.type === "domain" && own.has((p.domain ?? "").toLowerCase()))) {
      if (link === null || RANK[capped] > RANK[link]) link = capped;
      continue;
    }
    if (p.type === "domain") {
      outside.add(p.domain ?? "");
      continue;
    }
    const email = (p.emailAddress ?? "").toLowerCase();
    if (email === "") continue;
    const userId = p.type === "user" ? members[email] : undefined;
    if (userId === undefined) {
      outside.add(email);
      continue;
    }
    if (userId === ownerId) continue;
    const prev = byUser.get(userId);
    if (prev === undefined || RANK[capped] > RANK[prev]) byUser.set(userId, capped);
  }
  return {
    shares: [...byUser].map(([userId, role]) => ({ userId, role })).sort((a, b) => (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0)),
    link: link === null ? null : { scope: "workspace", role: link },
    outside: [...outside].filter((x) => x !== "").sort(),
  };
}

/** The review's starting choice: everyone with a member and no error, every shared drive someone can read. */
export function defaultDriveChoice(preview: Pick<GoogleDrivePreview, "people" | "sharedDrives">): GoogleDriveImportOptions {
  return {
    people: preview.people.filter((p) => p.memberId !== undefined && p.error === undefined).map((p) => p.email),
    sharedDrives: preview.sharedDrives.filter((d) => d.readAs !== undefined && d.error === undefined).map((d) => d.id),
  };
}

/** Bytes the chosen people's My Drives use (shared drives' sizes aren't known before listing). */
export function chosenDriveBytes(preview: Pick<GoogleDrivePreview, "people">, choice: GoogleDriveImportOptions): number {
  const chosen = new Set(choice.people);
  return preview.people.reduce((n, p) => n + (chosen.has(p.email) ? (p.bytes ?? 0) : 0), 0);
}

/** `p:<email>` for a person's My Drive, `d:<id>` for a shared drive. */
export function driveUnitKey(kind: "person" | "sharedDrive", id: string): string {
  return kind === "person" ? `p:${id.toLowerCase()}` : `d:${id}`;
}

/** Where "Open Files" goes after an import: the one top folder it made (rootItemIds), else Files' root (null). */
export function driveOpenFolder(rootItemIds: readonly string[]): string | null {
  return rootItemIds.length === 1 ? rootItemIds[0]! : null;
}

/** Overall percent of a running import (items done of items listed; 0 while nothing is listed). */
export function driveImportPercent(units: { done: number; total?: number | undefined; status: string }[]): number {
  let done = 0;
  let total = 0;
  for (const u of units) {
    if (u.status === "done") {
      done += u.total ?? u.done;
      total += u.total ?? u.done;
      continue;
    }
    if (u.total === undefined) continue;
    done += Math.min(u.done, u.total);
    total += u.total;
  }
  return total === 0 ? 0 : Math.floor((done / total) * 100);
}

/**
 * Where Settings → Import → Google Drive stands: no Google Workspace connection (`connect`); the last reading failed for
 * the missing Drive scope (`scope`); nothing read yet, or the last job was discarded (`ready`); reading (`scanning`);
 * choosing (`review`); copying (`importing`); finished, failed or cancelled after it started (`ended`).
 */
export type DriveImportStage = "connect" | "scope" | "ready" | "scanning" | "review" | "importing" | "ended";

export function driveImportStage(connected: boolean, job: { status: string; error?: { code: string } | undefined; options?: unknown } | null | undefined): DriveImportStage {
  if (job == null) return connected ? "ready" : "connect";
  if (job.status === "scanning") return "scanning";
  if (job.status === "review") return "review";
  if (job.status === "importing") return "importing";
  if (job.status === "failed" && job.error?.code === "drive_scope_missing") return "scope";
  if (job.status === "failed" && job.error?.code === "no_google") return "connect";
  // A job cancelled or failed before it was started (no options) is a reading that didn't finish.
  if (job.options == null) return connected ? "ready" : "connect";
  return "ended";
}

/** The review's choice when importing again: the earlier choice, kept to what can still be picked; else the default. */
export function driveChoiceFrom(preview: Pick<GoogleDrivePreview, "people" | "sharedDrives">, earlier: GoogleDriveImportOptions | null | undefined): GoogleDriveImportOptions {
  const def = defaultDriveChoice(preview);
  if (earlier == null) return def;
  const people = new Set(def.people);
  const drives = new Set(def.sharedDrives);
  return { people: earlier.people.filter((e) => people.has(e)), sharedDrives: earlier.sharedDrives.filter((d) => drives.has(d)) };
}

export interface DriveReviewSummary {
  people: number;
  sharedDrives: number;
  /** Bytes of the chosen My Drives (shared drives' sizes come while listing). */
  bytes: number;
  storageLeft: number;
  /** `bytes` is more than what's left: Start is off. */
  over: boolean;
  /** How much to uncheck or free up. */
  short: number;
  /** Nothing chosen: Start is off. */
  empty: boolean;
}

export function driveReviewSummary(preview: Pick<GoogleDrivePreview, "people" | "storageLeft">, choice: GoogleDriveImportOptions): DriveReviewSummary {
  const bytes = chosenDriveBytes(preview, choice);
  return {
    people: choice.people.length,
    sharedDrives: choice.sharedDrives.length,
    bytes,
    storageLeft: preview.storageLeft,
    over: bytes > preview.storageLeft,
    short: Math.max(0, bytes - preview.storageLeft),
    empty: choice.people.length + choice.sharedDrives.length === 0,
  };
}

export interface DriveImportTotals {
  /** Items done (files and folders), of `total` (absent until a unit is listed). */
  done: number;
  total?: number;
  bytes: number;
  skipped: number;
  percent: number;
  /** Units: done, needing attention, of all. */
  unitsDone: number;
  unitsAttention: number;
  units: number;
}

/** The running import's header line, summed over its units. */
export function driveImportTotals(units: { done: number; total?: number | undefined; bytes: number; skipped: number; status: string }[]): DriveImportTotals {
  let done = 0;
  let total = 0;
  let listed = false;
  let bytes = 0;
  let skipped = 0;
  let unitsDone = 0;
  let unitsAttention = 0;
  for (const u of units) {
    done += u.done;
    bytes += u.bytes;
    skipped += u.skipped;
    if (u.total !== undefined) {
      listed = true;
      total += u.total;
    }
    if (u.status === "done") unitsDone += 1;
    if (u.status === "attention") unitsAttention += 1;
  }
  return { done, ...(listed ? { total } : {}), bytes, skipped, percent: driveImportPercent(units), unitsDone, unitsAttention, units: units.length };
}

/** The not-copied list as CSV (RFC 4180): name, path, reason, link, time (ISO). */
export function driveProblemsCsv(problems: { unit: string; name: string; path: string; reason: string; link?: string | undefined; at: number }[]): string {
  const cell = (s: string) => (/[",\r\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s);
  const rows = [["Name", "Folder", "Reason", "Google link", "Time"].join(",")];
  for (const p of problems) rows.push([p.name, p.path, p.reason, p.link ?? "", new Date(p.at).toISOString()].map(cell).join(","));
  return rows.join("\r\n") + "\r\n";
}
